import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Copy, Eye, EyeOff, Trash2, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';
import { useSEO } from '../hooks/useSEO';

interface DecodedJWT {
  header: Record<string, any>;
  payload: Record<string, any>;
  signature: string;
  expiresAt?: Date;
  issuedAt?: Date;
  notBefore?: Date;
}

export default function JwtDecoder() {
  const [jwtInput, setJwtInput] = useState('');
  const [decodedData, setDecodedData] = useState<DecodedJWT | null>(null);
  const [error, setError] = useState('');
  const [showSignature, setShowSignature] = useState(false);

  useSEO({
    title: 'JWT Decoder & Analyzer - Decode JSON Web Tokens Online',
    description: 'Decode and analyze JSON Web Tokens (JWT) online. View header, payload, and signature information with expiration status and security insights.',
    keywords: 'JWT decoder, JSON Web Token, JWT analyzer, token decoder, JWT validation, security token, authentication token'
  });

  const decodeJWT = (token: string): DecodedJWT | null => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format. JWT should have 3 parts separated by dots.');
      }

      const [headerEncoded, payloadEncoded, signature] = parts;

      // Decode header
      const headerDecoded = atob(headerEncoded.replace(/-/g, '+').replace(/_/g, '/'));
      const header = JSON.parse(headerDecoded);

      // Decode payload
      const payloadDecoded = atob(payloadEncoded.replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(payloadDecoded);

      const result: DecodedJWT = {
        header,
        payload,
        signature
      };

      // Parse timestamps
      if (payload.exp) {
        result.expiresAt = new Date(payload.exp * 1000);
      }
      if (payload.iat) {
        result.issuedAt = new Date(payload.iat * 1000);
      }
      if (payload.nbf) {
        result.notBefore = new Date(payload.nbf * 1000);
      }

      return result;
    } catch (err) {
      throw new Error('Invalid JWT token. Please check the token format.');
    }
  };

  const handleInputChange = (value: string) => {
    setJwtInput(value);
    setError('');
    
    if (!value.trim()) {
      setDecodedData(null);
      return;
    }

    try {
      const decoded = decodeJWT(value.trim());
      setDecodedData(decoded);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JWT token');
      setDecodedData(null);
    }
  };

  const loadSampleJWT = () => {
    const sampleJWT = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyLCJleHAiOjE4MTYyMzkwMjJ9.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c';
    setJwtInput(sampleJWT);
    handleInputChange(sampleJWT);
    toast.success('Sample JWT loaded');
  };

  const clearInputs = () => {
    setJwtInput('');
    setDecodedData(null);
    setError('');
    setShowSignature(false);
    toast.success('Cleared all data');
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} copied to clipboard`);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const getTokenStatus = () => {
    if (!decodedData) return null;

    const now = new Date();
    
    if (decodedData.expiresAt) {
      if (now > decodedData.expiresAt) {
        return {
          status: 'expired',
          color: 'red',
          icon: XCircle
        };
      } else {
        return {
          status: 'valid',
          color: 'green',
          icon: CheckCircle
        };
      }
    }
    
    return {
      status: 'no-expiry',
      color: 'yellow',
      icon: Clock
    };
  };

  const renderJsonValue = (key: string, value: any): React.ReactNode => {
    if (value === null) return (
      <div className="ml-4">
        <span className="text-blue-600 font-medium">"{key}"</span>: 
        <span className="ml-2 text-gray-500">null</span>
      </div>
    );

    if (typeof value === 'object') {
      return (
        <div className="ml-4">
          <span className="text-blue-600 font-medium">"{key}"</span>: {Array.isArray(value) ? '[' : '{'}
          <div className="ml-4">
            {Object.entries(value).map(([k, v]) => (
              <div key={k}>{renderJsonValue(k, v)}</div>
            ))}
          </div>
          {Array.isArray(value) ? ']' : '}'}
        </div>
      );
    }

    const isTimestamp = (key === 'exp' || key === 'iat' || key === 'nbf') && typeof value === 'number';
    
    return (
      <div className="ml-4">
        <span className="text-blue-600 font-medium">"{key}"</span>: 
        <span className={`ml-2 ${typeof value === 'string' ? 'text-green-600' : 'text-purple-600'}`}>
          {typeof value === 'string' ? `"${value}"` : value?.toString()}
        </span>
        {isTimestamp && (
          <span className="ml-2 text-xs text-gray-500">
            ({formatTimestamp(value)})
          </span>
        )}
      </div>
    );
  };

  const tokenStatus = getTokenStatus();

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Shield className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            JWT Decoder & Analyzer
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Decode and analyze JSON Web Tokens (JWT) with comprehensive validation. View header, payload, 
            and signature information with expiration status and security insights.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        {/* Input Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8 mb-6 sm:mb-8 hover:shadow-xl transition-all duration-300"
        >
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
              <Shield className="h-5 w-5 mr-2 text-[#8438ff]" />
              JWT Token Input
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={loadSampleJWT}
                className="px-3 py-1.5 text-xs sm:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 font-medium shadow-sm"
              >
                Load Sample JWT
              </button>
              <button
                onClick={clearInputs}
                className="p-1.5 sm:p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200"
                title="Clear all"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          <textarea
            value={jwtInput}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder="Paste your JWT token here..."
            className="w-full h-32 sm:h-40 p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl resize-none focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors font-mono text-xs sm:text-sm"
          />

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            </div>
          )}

          {decodedData && tokenStatus && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              className={`mt-4 p-4 rounded-xl border ${
                tokenStatus.color === 'red' 
                  ? 'bg-red-50 border-red-200' 
                  : tokenStatus.color === 'yellow'
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-green-50 border-green-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <tokenStatus.icon className={`h-5 w-5 text-${tokenStatus.color}-500 mr-3`} />
                  <div>
                    <span className={`text-${tokenStatus.color}-700 text-sm font-semibold`}>
                      {tokenStatus.status === 'expired' && 'Token Expired'}
                      {tokenStatus.status === 'valid' && 'Token Valid'}
                      {tokenStatus.status === 'no-expiry' && 'Token Valid (No Expiry)'}
                    </span>
                    {decodedData.expiresAt && (
                      <p className="text-xs text-gray-600 mt-1">
                        Expires: {decodedData.expiresAt.toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Decoded Sections */}
        {decodedData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid lg:grid-cols-2 gap-6 sm:gap-8"
          >
            {/* Header Section */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between mb-5 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Header
                </h3>
                <button
                  onClick={() => copyToClipboard(JSON.stringify(decodedData.header, null, 2), 'Header')}
                  className="flex items-center px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-all duration-200 font-medium"
                  title="Copy header"
                >
                  <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                  Copy
                </button>
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-5 font-mono text-xs sm:text-sm overflow-x-auto border">
                <div className="text-gray-700 leading-relaxed">
                  <div className="text-purple-600 font-semibold">{'{'}</div>
                  <div className="ml-4">
                    {Object.entries(decodedData.header).map(([key, value], index, array) => (
                      <div key={key} className="py-1">
                        <span className="text-blue-600 font-medium">"{key}"</span>
                        <span className="text-gray-500">: </span>
                        <span className="text-green-600">"{value}"</span>
                        {index < array.length - 1 && <span className="text-gray-500">,</span>}
                      </div>
                    ))}
                  </div>
                  <div className="text-purple-600 font-semibold">{'}'}</div>
                </div>
              </div>
            </div>

            {/* Payload Section */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between mb-5 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  Payload
                </h3>
                <button
                  onClick={() => copyToClipboard(JSON.stringify(decodedData.payload, null, 2), 'Payload')}
                  className="flex items-center px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-all duration-200 font-medium"
                  title="Copy payload"
                >
                  <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                  Copy
                </button>
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-5 font-mono text-xs sm:text-sm overflow-x-auto border max-h-96 overflow-y-auto">
                <div className="text-gray-700 leading-relaxed">
                  <div className="text-purple-600 font-semibold">{'{'}</div>
                  <div className="ml-2">
                    {Object.entries(decodedData.payload).map(([key, value]) => (
                      <div key={key}>{renderJsonValue(key, value)}</div>
                    ))}
                  </div>
                  <div className="text-purple-600 font-semibold">{'}'}</div>
                </div>
              </div>
            </div>

            {/* Signature Section */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8 lg:col-span-2 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between mb-5 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                  Signature
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowSignature(!showSignature)}
                    className="flex items-center px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-all duration-200 font-medium"
                    title={showSignature ? 'Hide signature' : 'Show signature'}
                  >
                    {showSignature ? <EyeOff className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" /> : <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />}
                    {showSignature ? 'Hide' : 'Show'}
                  </button>
                  {showSignature && (
                    <button
                      onClick={() => copyToClipboard(decodedData.signature, 'Signature')}
                      className="flex items-center px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-all duration-200 font-medium"
                      title="Copy signature"
                    >
                      <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                      Copy
                    </button>
                  )}
                </div>
              </div>
              
              {showSignature ? (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-5 font-mono text-xs sm:text-sm break-all border">
                  <div className="text-gray-700">{decodedData.signature}</div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
                  <Shield className="h-10 w-10 text-yellow-500 mx-auto mb-3" />
                  <p className="text-yellow-700 text-sm font-medium">
                    Signature is hidden for security. Click "Show" to reveal.
                  </p>
                </div>
              )}

              <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Security Note
                </h4>
                <p className="text-xs text-blue-800 leading-relaxed">
                  This tool only decodes JWT tokens client-side. It does NOT verify signatures. 
                  For production use, always verify signatures server-side with the proper secret or public key.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* JWT Information */}
        {!decodedData && !error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8"
          >
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Shield className="h-5 w-5 mr-2 text-[#8438ff]" />
              About JWT (JSON Web Tokens)
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Structure</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></div>
                    <div>
                      <strong>Header:</strong> Contains algorithm and token type
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                    <div>
                      <strong>Payload:</strong> Contains claims and user data
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></div>
                    <div>
                      <strong>Signature:</strong> Verifies token integrity
                    </div>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Common Claims</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li><strong>sub:</strong> Subject (user identifier)</li>
                  <li><strong>iss:</strong> Issuer</li>
                  <li><strong>aud:</strong> Audience</li>
                  <li><strong>exp:</strong> Expiration time</li>
                  <li><strong>iat:</strong> Issued at</li>
                  <li><strong>nbf:</strong> Not before</li>
                </ul>
              </div>
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8 sm:mt-12"
        />
      </div>
    </div>
  );
}
